import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';

// Reemplaza con tu URL real del Worker después del despliegue
const WORKER_URL = 'https://website-monitor.fpantojaz87.workers.dev';

function App() {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [stats, setStats] = useState({ total: 0, storage: 0 });

  const showMessage = useCallback((text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  }, []);

  const loadScreenshots = useCallback(async () => {
    try {
      const response = await axios.get(`${WORKER_URL}/api/screenshots`);
      setScreenshots(response.data.screenshots || []);
      setStats({
        total: response.data.total || 0,
        storage: (response.data.screenshots || []).reduce((acc, shot) => acc + (shot.size || 0), 0)
      });
    } catch (error) {
      console.error('Error loading screenshots:', error);
      showMessage('Error loading screenshots', 'error');
    }
  }, [showMessage]);

  useEffect(() => {
    loadScreenshots();
  }, [loadScreenshots]);

  const takeScreenshot = async () => {
    if (!websiteUrl.trim()) {
      showMessage('Please enter a website URL', 'error');
      return;
    }

    // Validar URL básica
    let validatedUrl = websiteUrl.trim();
    if (!validatedUrl.startsWith('http')) {
      validatedUrl = `https://${validatedUrl}`;
    }

    try {
      new URL(validatedUrl);
    } catch (e) {
      showMessage('Please enter a valid URL', 'error');
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await axios.post(`${WORKER_URL}/api/screenshot`, {
        websiteUrl: validatedUrl
      });

      if (response.data.success) {
        showMessage('Screenshot taken successfully!', 'success');
        setWebsiteUrl('');
        // Esperar un poco antes de recargar para dar tiempo al procesamiento
        setTimeout(loadScreenshots, 2000);
      } else {
        showMessage('Error taking screenshot: ' + (response.data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Network error';
      showMessage(`Error taking screenshot: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const triggerCleanup = async () => {
    if (!window.confirm('This will delete all screenshots older than 1 week. Continue?')) {
      return;
    }

    try {
      const response = await axios.post(`${WORKER_URL}/api/cleanup`);
      showMessage(response.data.message, 'success');
      setTimeout(loadScreenshots, 1000);
    } catch (error) {
      console.error('Error during cleanup:', error);
      const errorMessage = error.response?.data?.error || error.message;
      showMessage(`Error during cleanup: ${errorMessage}`, 'error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      takeScreenshot();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1>🌐 Website Monitor</h1>
          <p>Automated website screenshot capture and management</p>
        </div>
        
        <div className="stats-bar">
          <div className="stat">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Screenshots</span>
          </div>
          <div className="stat">
            <span className="stat-number">{formatFileSize(stats.storage)}</span>
            <span className="stat-label">Storage Used</span>
          </div>
        </div>
        
        <div className="input-section">
          <div className="url-input-container">
            <input
              type="url"
              placeholder="Enter website URL (e.g., example.com or https://example.com)"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              className="url-input"
              disabled={loading}
            />
            <button 
              onClick={takeScreenshot} 
              disabled={loading}
              className={`capture-btn ${loading ? 'loading' : ''}`}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Capturing...
                </>
              ) : (
                '📸 Take Screenshot'
              )}
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="actions">
          <button 
            onClick={triggerCleanup}
            className="cleanup-btn"
            title="Delete screenshots older than 1 week"
          >
            🗑️ Cleanup Old Screenshots
          </button>
          
          <button 
            onClick={loadScreenshots}
            className="refresh-btn"
          >
            🔄 Refresh
          </button>
        </div>

        <div className="screenshots-section">
          <h2>Recent Screenshots</h2>
          {screenshots.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📷</div>
              <h3>No screenshots yet</h3>
              <p>Take your first screenshot by entering a URL above!</p>
            </div>
          ) : (
            <>
              <div className="screenshots-grid">
                {screenshots.map((screenshot, index) => (
                  <div key={screenshot.filename} className="screenshot-card">
                    <div className="screenshot-image-container">
                    <img 
                      src={`${WORKER_URL}/screenshot/${screenshot.filename}`}
                      alt={`Screenshot of ${screenshot.domain} taken on ${formatDate(screenshot.capturedAt)}`}
                      className="screenshot-image"
                      onError={(e) => {
                        // ✅ SOLUCIÓN ROBUSTA: SVG inline como fallback
                        const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200">
                          <rect width="100%" height="100%" fill="#667eea"/>
                          <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" 
                                font-family="Arial, sans-serif" font-size="14" font-weight="bold">
                            Image Not Available
                          </text>
                          <text x="50%" y="65%" dominant-baseline="middle" text-anchor="middle" fill="white" 
                                font-family="Arial, sans-serif" font-size="12">
                            ${screenshot.domain}
                          </text>
                        </svg>`;
                        e.target.src = `data:image/svg+xml;base64,${btoa(svgString)}`;
                      }}
                    />
                      <div className="screenshot-overlay">
                        <button 
                          className="view-btn"
                          onClick={() => window.open(`${WORKER_URL}/screenshot/${screenshot.filename}`, '_blank')}
                        >
                          👁️ View Full Size
                        </button>
                      </div>
                    </div>
                    <div className="screenshot-info">
                      <h4 className="screenshot-domain">{screenshot.domain}</h4>
                      <p className="screenshot-filename">{screenshot.filename}</p>
                      <div className="screenshot-meta">
                        <span className="meta-item">📅 {formatDate(screenshot.capturedAt)}</span>
                        <span className="meta-item">💾 {formatFileSize(screenshot.size)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </header>
    </div>
  );
}

export default App;
